import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import { Badge } from '@/components/ui/badge';

import { useEmailServerStatus } from '../api';

/**
 * Live SES connection badge for one server, queried on mount (so the tab shows status on load).
 * Green when {@code GetAccount} succeeds; red with the failure code on hover otherwise.
 */
export function EmailServerStatusBadge({ serverId }: { readonly serverId: string }): ReactElement {
  const { t } = useTranslation();
  const status = useEmailServerStatus(serverId);

  if (status.isPending) {
    return <Badge variant="outline">{t('routes.admin.email.status.checking')}</Badge>;
  }

  if (status.isError) {
    return (
      <Badge variant="destructive" title={t('routes.admin.email.status.failedTitle', { code: getErrorCodeForUi(status.error) })}>
        {t('routes.admin.email.status.failed')}
      </Badge>
    );
  }

  const data = status.data;
  const sesBadge = data.ok ? (
    <Badge variant="secondary" title={t('routes.admin.email.status.okTitle')}>
      {t('routes.admin.email.status.ok')}
    </Badge>
  ) : (
    <Badge
      variant="destructive"
      title={t('routes.admin.email.status.failedDetail', {
        code: data.error_code ?? 'unknown',
        detail: data.detail ?? '',
      })}
    >
      {t('routes.admin.email.status.failed')}
    </Badge>
  );

  // The delivery-feedback SQS queue is optional; only show its check when one is configured.
  const queueBadge = !data.queue_configured ? null : (data.queue_ok ?? false) ? (
    <Badge variant="secondary" title={t('routes.admin.email.status.queueOkTitle')}>
      {t('routes.admin.email.status.queueOk')}
    </Badge>
  ) : (
    <Badge
      variant="destructive"
      title={t('routes.admin.email.status.queueFailedDetail', {
        code: data.queue_error_code ?? 'unknown',
        detail: data.queue_detail ?? '',
      })}
    >
      {t('routes.admin.email.status.queueFailed')}
    </Badge>
  );

  return (
    <span className="inline-flex items-center gap-1.5">
      {sesBadge}
      {queueBadge}
    </span>
  );
}
